// ModernBrushPanel — Modern 常設「ブラシ」パネル (デスクトップ版、**タブ構成**).
// (docs/_wip/modern-brush-panel-2026-08-17.md §14「タブ構成」consult 2026-08-18 確定).
//
// 背景 (§0-§4): エアブラシ追加でツールプロパティが縦に溢れた (ペン種チップが4つになり
// 2行折り返し + airbrush 専用スライダが2本増えた)。対症療法でなく構造的に解決する:
// **ペン種チップ (= 「どのブラシか」) を独立の常設パネルへ切り出す**。CSP でいう
// 「サブツールパレット」への収束であり、§3 カスタムブラシ (保存 / 採取ブラシ) が
// 確定スコープである以上、遅かれ早かれパネル以外の受け皿が無い (§1)。
//
// **分担 (§3)**: 「どのブラシか」= 本パネル。「そのブラシをどう調整するか」(太さ /
// 不透明度 / 番手 / ペン別パラメータ) は引き続きツールプロパティ側 (`PenParamsSection`)
// — grain/density のような「描きながら調整する値」をパネル開閉の裏に隠さないため。
//
// ## §14 タブ構成 (consult 2026-08-18、旧グループ構造からの作り直し)
//
// 旧実装 (グループ + 見出し) は §4.1 の一般ルールで縦圧迫を解消したが、エアブラシに続く
// 将来ペン (ドット/ぼかし/油彩/本水彩/クレヨン) を迎えるたびに「どのグループに入れるか」
// の再編が要る**不安定な構造**だった。タブに作り直し、**性格別 (実装区分でなく「何を
// 描きたいか」) に固定 4 タブ**で受ける (§14.2、最重要判断):
//
//   | タブ | 現在の中身 | 将来の指定席 |
//   |---|---|---|
//   | 線画 | ブラシ, サインペン, ドット, トーン | (Phase 11 で充足済) |
//   | 塗り | エアブラシ | ぼかし, 油彩, 本水彩 |
//   | 質感 | 紙, 毛先, 雲, カケアミ, クレヨン, パステル | (Phase 11 で充足済) |
//   | マイブラシ | (準備中) | カスタムブラシ, 採取ブラシ (§3) |
//
// **クレヨン / パステル (phase11-light-pens-2026-08-21.md §2/§6)**: 質感タブに 6 項目目
// として追加 (Phase 11 軽量ペン、表現パリティ原則で Classic 同時投入)。**ドット / トーン
// (同 §3/§4/§6)**: 線画タブに 4 項目目・3 項目目として追加 (ドットは Wasm parity 解消済み
// で新規露出、トーンは 2026-06-20 の封印を pitch パラメータ化で解除)。狭ドック (180px) /
// phone は `BRUSH_PANEL_ITEMS_MAX_HEIGHT` の scroll 保険で吸収する (上部コメント
// 「`max-height`」参照)。
//
// **「その他」タブを作らないことが設計の核心** (§14.2)。新ペンを追加する人は必ず上表の
// どこかに入れられるはずで、器 (タブの本数) を作り直す必要が無い。新ペンを追加すると
// きは、まずこの表 (と下記 `BRUSH_PANEL_TABS` のコメント) を見て指定席を確認すること。
//
// **タブは常時開いており、タブ内は 1 タップ**。プルダウン (§2 で却下) の 2 タップ・
// ゴーストクリック罠のどちらにも触れない (§14.1)。
//
// **見出し (`<h3>`) は廃止**: タブのラベルが見出しを兼ねるため、旧グループ見出しは不要に
// なった (旧「テクスチャ」見出しはタブラベル「質感」に統合)。
//
// **既定タブ = 線画 / 選択中ブラシを含むタブへ自動追従** (§14.3): 既定ペン `brush` は
// 線画タブに属する。ショートカット等でペンが外部から変わったとき、そのペンを含むタブが
// 無いと「選択中のブラシが画面のどこにも無い」状態になる (§9.11 の教訓と同型) ので、
// `pen` prop の変化を追跡して自動でタブを切り替える (下記 `useEffect` 参照)。
//
// **`BrushSamplePreview` (`ModernToolProperties`) は撤去しない** (§14.3): 選択中ブラシが
// 別タブにある間、これが唯一の視覚フィードバックになる (fable-advisor 2026-08-17 の指摘)。
//
// **タブ選択状態は localStorage のみに永続** (`modernBrushPanelTabPreference.ts`)。
// `WorkspaceState` には入れない (= §6 の workspace migration に触れない、既存パネル
// 配置と混同しない)。永続値より「現在の pen が属するタブ」を優先する順序判断は
// `initialTabFor` 参照。
//
// **マイブラシはタブ化** (§14.3): 旧「常設 1 行フッタ」は廃止し、タブの 1 つにする
// (= 常時場所を取るのをやめる)。空のときはタブを開いた時だけ「準備中」の空状態を表示。
//
// **`max-height` (`min(300px, 45dvh)`) は保険として残す** (`brushPanelShared.ts`)。今日の
// 中身 (最大 4 item/タブ) では効かなくなるはずだが、カスタムブラシで 1 タブが伸びたときに
// 効く。
//
// ## フォン版との分離
//
// フォン (`ModernPhoneShell`) はタブを持ち込まない (§14 実装注記)。全ブラシを並べる旧
// 実装は `ModernBrushPanelFlat.tsx` に凍結維持し、`ModernLayout.tsx` が `isMobile` で
// 出し分ける。item のスタイル・サムネ計算 (リスト表示固定) は両者で `brushPanelShared.ts`
// を共有する。
//
// **サムネイル**: `drawing-core` の `renderBrushSample` (= 本物の stroke レンダラ) で
// 各 item の描き味を「～」に描く。airbrush は `penParams` (seed/grain/density) を渡さないと
// `resolveStampRenderer` が noop レンダラを返し空白になる (2026-08 発覚のバグ、
// `BrushSamplePreview` と同じ罠) ので、固定 seed + 現在の grain/density を必ず埋める。
// texture item は自分自身の preset を固定で渡す。
//
// **メモ化 (§14 実装注記「非アクティブタブのサムネまで毎回描かない」)**: タブ化により
// 表示される item は常に「アクティブタブぶんだけ」なので、`renderBrushSample` の計算も
// アクティブタブの item だけに絞る (旧実装は全 item を 1 回の `useMemo` で一括計算して
// いたが、タブが増えるほど無駄な計算が増えるため、タブ切替のたびに対象を絞り直す)。
//
// **a11y (`tablist`/`tab`/`tabpanel` + 矢印キー移動)**: `ColorPanel.tsx` の
// `role="tablist"`/`role="tab"` (数値表記タブ) が biome `useSemanticElements` を通過して
// いる既存の前例に倣う (= radio と違い tab ロールに要求される固有 HTML 要素は無いので
// 制約に当たらない)。矢印キー (←→) + Home/End でタブ間を移動する roving tabindex を
// 実装する (= 選択中タブだけ `tabIndex=0`、他は `-1`、Tab キーはタブ列を素通りして
// `tabpanel` へ抜ける標準の WAI-ARIA Tabs パターン)。item ボタン自体は旧実装と同じ
// `<button aria-pressed>` 群 (`role="radio"` 回避、`ModernToolProperties.tsx` の手ぶれ補正
// segmented と同じ回避策)。
//
// ## 狭ドック 180px 対応 (2026-08-19、マイブラシタブのみアイコン化)
//
// 実機フィードバックで、狭ドック (180px) だとタブ行 `線画/塗り/質感/マイブラシ` が
// 収まらない/読めない問題が判明した。原因は「マイブラシ」だけ 5 文字であること
// (他 3 つは 2 文字)。`truncate` で行崩れ自体は防げても、5 文字ラベルが 1〜2 文字に
// 千切れて読めなくなるのでは本末転倒。対処は **「マイブラシ」タブ 1 つだけをアイコン化**
// する (線画/塗り/質感はテキストラベルのまま)。4 つ全部でなく 1 つだけに留める理由
// (ユーザー判断 2026-08-19):
//
// 1. **分類の軸がそもそも違う**: 線画/塗り/質感は「表現の性格」による分類、マイブラシは
//    「所有 (自分のものかどうか)」による分類。見た目の扱いを変えるのはこの分類構造の
//    違いを反映しているのであって恣意的な線引きではない。
// 2. **記号の可読性コストが 4 つで同じでない**: ★やブックマークのような「自分のもの」を
//    表す記号はすでに世の中で広く読めるが、「線画」「塗り」「質感」を表すアイコンは
//    (何を描いても) 発明が要り学習コストがかかる。アイコン化のコストが一様でない以上、
//    コストの低い 1 つだけをアイコン化するのが妥当。
// 3. **幅超過の原因はマイブラシの 5 文字だけ**なので、そこだけ潰せば足りる (他 3 つを
//    アイコン化する必要が無い)。
//
// マイブラシタブは常に `MyBrushIcon` のみを表示し、テキストラベルは出さない (アクティブ/
// 非アクティブで表示を出し分ける仕組みは持たない)。`aria-label`/`title` には常にフルの
// 「マイブラシ」を持たせ、スクリーンリーダー/マウスいずれの環境でも名前を失わない (線画/
// 塗り/質感の 3 タブは可視テキストがそのまま accessible name になるので追加の
// `aria-label` は付けない、既存の慣行のまま)。
//
// ## ヘッダ (見出し + リスト/グリッド切替) の撤去、リスト表示固定 (2026-08-19)
//
// **`<h2>ブラシ</h2>` を撤去** (ユーザー判断 2026-08-18): タブのラベル + item 群を見れば
// このパネルが何かは自明で、見出しは情報を足していない。**先行事例**として
// `ModernColorPanel.tsx` は既に見出し無しで運用されている (カラーホイールは見た目で
// 自明なので見出しが要らない、という実例)。パネル名自体が失われるわけではない:
// `<section aria-label="ブラシ">` (支援技術) / `ModernPanelDragShell` のグリップ帯の
// `aria-label`/`title` (マウス hover) / `ModernPanelVisibilityMenu` (パネル名一覧) が
// 引き続き名前を提供する。**a11y**: `<section aria-label="ブラシ">` は残す。
//
// 他パネル (`ModernLayerPanel`/`ModernPreviewPanel`/`ModernColorPalette`) は `<header>` で
// パネル名を出しているが、同じ理由 (中身を見れば自明) で見出しを撤去する方向はあり得る
// — ただしそれは**別コミットの範囲**であり、今回のスコープはブラシパネルのみ。
//
// **リスト/グリッド切替を撤去し、リスト固定にする**: グリッド表示の存在理由は「一覧が
// 長いときのコンパクトさ」だったが、タブ化 (§14) で 1 タブあたり最大 4 item になり、その
// 理由が消えた。機能を減らして簡素にする (`ModernBrushPanelFlat.tsx` も同時撤去、片方だけ
// 残すと desktop/phone で挙動が食い違うため)。`modernBrushPanelViewPreference.ts` (旧
// list/grid 永続ストア) は両バリアントから参照が無くなったので削除済み。**タブの永続
// (`modernBrushPanelTabPreference.ts`) は別物、混同しないこと**。
//
// 将来の申し送り: カスタムブラシが入って「マイブラシ」タブが長くなったら、**そのタブの
// 中だけ**グリッド表示を再導入する余地がある (= additive にタブ内へ追加できる、パネル
// 構造の作り直しにはならない)。今は「1 タブ最大 4 item」なので不要という判断。

import type { StrokePen } from '@oekaki/protocol';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { PEN_TO_TAB as DOMAIN_PEN_TO_TAB } from '../../../domain/PenTabs.js';
import {
  type BrushPanelTabId,
  createModernBrushPanelTabPreferenceStore,
  DEFAULT_BRUSH_PANEL_TAB,
} from '../../../infrastructure/modernBrushPanelTabPreference.js';
import { TEXTURE_PRESET_OPTIONS } from '../shared/TexturePresetPicker.js';
import { BrushPanelItemButton } from './BrushPanelItemButton.js';
import {
  BRUSH_PANEL_ITEMS_MAX_HEIGHT,
  type BrushPanelItem,
  computeBrushPanelSamples,
  ITEMS_CONTAINER_CLASS,
  type ItemSample,
  type ModernBrushPanelProps,
} from './brushPanelShared.js';
import { MyBrushIcon } from './ModernIcons.js';
import { MODERN_BRUSH_SUBTOOLS } from './modernTools.js';

export type { ModernBrushPanelProps } from './brushPanelShared.js';

interface BrushPanelTab {
  readonly id: BrushPanelTabId;
  readonly label: string;
  readonly items: ReadonlyArray<BrushPanelItem>;
}

const findSubtool = (
  pen: Extract<
    StrokePen,
    'brush' | 'flat' | 'texture' | 'tone' | 'airbrush' | 'crayon' | 'pastel' | 'dot'
  >,
): BrushPanelItem => {
  const sub = MODERN_BRUSH_SUBTOOLS.find((s) => s.pen === pen);
  // MODERN_BRUSH_SUBTOOLS は全ペン種を含む静的テーブルなので、ここで呼ぶ pen は
  // 必ず見つかる (見つからなければ MODERN_BRUSH_SUBTOOLS 側の記述漏れ、テストで検出)。
  if (!sub) throw new Error(`modernTools.ts に pen='${pen}' の定義がありません`);
  return { key: sub.pen, label: sub.label, pen: sub.pen };
};

/**
 * タブ構成の唯一の定義元 (§14.2 の表そのもの)。**新しいペンを追加するときは、まずこの
 * 配列のどのタブに入れるかを決める** — 「その他」タブは無い (§14.2 が設計の核心)。
 * 迷ったら §14.2 の表 (本ファイル冒頭コメント) を見る。
 */
const BRUSH_PANEL_TABS: ReadonlyArray<BrushPanelTab> = [
  {
    id: 'line',
    label: '線画',
    // ドット / トーン (phase11-light-pens-2026-08-21.md §3/§4/§6) を末尾に追加
    // (Phase 11 軽量ペン + 封印解除、線画タブが4項目になる)。
    items: [findSubtool('brush'), findSubtool('flat'), findSubtool('dot'), findSubtool('tone')],
  },
  {
    id: 'fill',
    label: '塗り',
    // 将来の指定席: ぼかし / 油彩 / 本水彩 (§14.2)。
    items: [findSubtool('airbrush')],
  },
  {
    id: 'texture',
    label: '質感',
    // クレヨン / パステル (phase11-light-pens-2026-08-21.md §2/§6) を末尾に追加
    // (Phase 11 軽量ペン、質感タブが6項目になる)。
    items: [
      ...TEXTURE_PRESET_OPTIONS.map((opt) => ({
        key: `texture:${opt.id}`,
        label: opt.label,
        pen: 'texture' as const,
        texturePreset: opt.id,
      })),
      findSubtool('crayon'),
      findSubtool('pastel'),
    ],
  },
  {
    id: 'myBrush',
    label: 'マイブラシ',
    // 将来の指定席: カスタムブラシ, 採取ブラシ (§3 着手時、今は空の器のみ)。
    items: [],
  },
];

/**
 * 現在の pen を含むタブ ID (無ければ null、= 消しゴム等タブに属さない pen)。
 *
 * 2026-08-21 (CSP流 循環ショートカット): 「どの pen がどのタブに属するか」の判断は
 * `domain/PenTabs.ts` (`PEN_TO_TAB`) が single source of truth になった (= ショート
 * カット循環アルゴリズムと自動追従が同じ表を見る、shortcut-settings.md「CSP の実
 * モデル」)。`myBrush` タブ (= domain 側に無い、空の器のみ) は元々どの pen も持たない
 * ので参照しなくても等価。
 */
const tabForPen = (pen: StrokePen): BrushPanelTabId | null => DOMAIN_PEN_TO_TAB[pen] ?? null;

/**
 * 初期タブの決定順位: **現在の pen が属するタブ最優先** > 永続化された前回選択 >
 * 既定 (線画)。pen 優先にする理由は上部コメント参照 (= 選択中ブラシが画面のどこにも
 * 無い状態を mount 時点から作らないため)。
 */
const initialTabFor = (pen: StrokePen, persisted: BrushPanelTabId | null): BrushPanelTabId =>
  tabForPen(pen) ?? persisted ?? DEFAULT_BRUSH_PANEL_TAB;

/**
 * ブラシパネルの本体 (デスクトップ版)。タブ (線画/塗り/質感/マイブラシ) で「どの
 * ブラシか」を選ぶ。表示切替 (リスト⇄グリッド) はアクティブなタブの中に適用する。
 */
export const ModernBrushPanel = ({
  pen,
  onSelectPen,
  onSelectTexturePreset,
  width,
  alpha,
  pressure,
  texturePreset,
  airbrushGrain,
  airbrushDensity,
}: ModernBrushPanelProps): React.ReactNode => {
  const tabStoreRef = useRef(createModernBrushPanelTabPreferenceStore());
  const [activeTab, setActiveTabState] = useState<BrushPanelTabId>(() =>
    initialTabFor(pen, tabStoreRef.current.get()),
  );
  const selectTab = (id: BrushPanelTabId): void => {
    setActiveTabState(id);
    tabStoreRef.current.set(id);
  };

  // 外部からペンが変わったときの自動追従 (§14.3)。`pen` が実際に変化した回だけ判定する
  // (= ユーザーが手でタブを開いているときに、無関係な再 render で引き戻されないように
  // 前回値を ref で追跡する)。tabForPen が null (= 消しゴム等タブに属さない pen) の
  // ときは追従せず、ユーザーが見ていたタブをそのまま保つ。
  const prevPenRef = useRef(pen);
  // selectTab は state setter + storage 書き込みのみで参照が安定していないため、
  // 依存配列には含めない (= useExhaustiveDependencies が意図した「pen の変化検知」を
  // 壊さないよう pen だけを見る、同種の意図的な絞り込みは brushPanelShared.ts の
  // `computeBrushPanelSamples` 呼び出し側でも行っている)。
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectTab は都度再生成されるが安定した動作 (setState + storage write) なので pen の変化検知だけを依存にする
  useEffect(() => {
    if (pen === prevPenRef.current) return;
    prevPenRef.current = pen;
    const next = tabForPen(pen);
    if (next !== null) selectTab(next);
  }, [pen]);

  const currentTab = BRUSH_PANEL_TABS.find((t) => t.id === activeTab) ?? BRUSH_PANEL_TABS[0];
  const items: ReadonlyArray<BrushPanelItem> = currentTab?.items ?? [];

  // アクティブタブぶんだけ計算する (= 非アクティブタブのサムネは描かない、上部コメント
  // 「メモ化」参照)。`items` は `BRUSH_PANEL_TABS` (モジュール定数) から `.find` で
  // 引いた既存配列への参照なので、同じタブが選ばれている限り参照は安定している。
  const samples = useMemo<ReadonlyArray<ItemSample>>(
    () =>
      computeBrushPanelSamples(items, {
        width,
        alpha,
        pressure: {
          widthEnabled: pressure.widthEnabled,
          widthMin: pressure.widthMin,
          alphaEnabled: pressure.alphaEnabled,
          alphaMin: pressure.alphaMin,
        },
        airbrushGrain,
        airbrushDensity,
      }),
    [
      items,
      width,
      alpha,
      pressure.widthEnabled,
      pressure.widthMin,
      pressure.alphaEnabled,
      pressure.alphaMin,
      airbrushGrain,
      airbrushDensity,
    ],
  );

  const canvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
  useEffect(() => {
    for (const s of samples) {
      const canvas = canvasRefs.current.get(s.key);
      if (!canvas) continue;
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      ctx.putImageData(new ImageData(s.sample.rgba, s.sample.width, s.sample.height), 0, 0);
    }
  }, [samples]);

  const selectItem = (item: BrushPanelItem): void => {
    onSelectPen(item.pen);
    if (item.texturePreset !== undefined) onSelectTexturePreset(item.texturePreset);
  };

  // roving tabindex (WAI-ARIA Tabs パターン): 矢印キー/Home/End でタブ間を移動し、
  // フォーカスも一緒に動かす。Tab キーはタブ列を素通りして tabpanel へ抜ける
  // (= 選択中タブだけ tabIndex=0、他は -1)。
  const tabButtonRefs = useRef<Partial<Record<BrushPanelTabId, HTMLButtonElement>>>({});
  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
    let nextIndex: number | null = null;
    if (e.key === 'ArrowRight') nextIndex = (index + 1) % BRUSH_PANEL_TABS.length;
    else if (e.key === 'ArrowLeft')
      nextIndex = (index - 1 + BRUSH_PANEL_TABS.length) % BRUSH_PANEL_TABS.length;
    else if (e.key === 'Home') nextIndex = 0;
    else if (e.key === 'End') nextIndex = BRUSH_PANEL_TABS.length - 1;
    if (nextIndex === null) return;
    e.preventDefault();
    const next = BRUSH_PANEL_TABS[nextIndex];
    if (!next) return;
    selectTab(next.id);
    tabButtonRefs.current[next.id]?.focus();
  };

  const idPrefix = useId();
  const tabDomId = (id: BrushPanelTabId): string => `${idPrefix}-tab-${id}`;
  const panelDomId = (id: BrushPanelTabId): string => `${idPrefix}-tabpanel-${id}`;

  return (
    <section
      className="flex flex-col gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-2"
      aria-label="ブラシ"
    >
      {/* タブ列 (§14): 線画/塗り/質感/マイブラシ。狭ドック 180px 対応 (上部コメント
          「狭ドック 180px 対応」参照): マイブラシだけアイコン化 (`MyBrushIcon`)、他 3 つは
          テキストラベルのまま常時表示 (アクティブ/非アクティブで出し分けない)。 */}
      <div role="tablist" aria-label="ブラシの種類" className="flex gap-1">
        {BRUSH_PANEL_TABS.map((tab, index) => {
          const selected = tab.id === activeTab;
          const isMyBrush = tab.id === 'myBrush';
          return (
            <button
              key={tab.id}
              ref={(el) => {
                if (el) tabButtonRefs.current[tab.id] = el;
                else delete tabButtonRefs.current[tab.id];
              }}
              type="button"
              role="tab"
              id={tabDomId(tab.id)}
              aria-selected={selected}
              aria-controls={panelDomId(tab.id)}
              // マイブラシだけ、可視ラベルが無い (アイコンのみ) ぶん aria-label/title で
              // フルの名前を常に持たせる。他 3 つは可視テキストがそのまま accessible name
              // になるので付けない (既存の慣行のまま)。
              aria-label={isMyBrush ? tab.label : undefined}
              title={isMyBrush ? tab.label : undefined}
              data-manual={isMyBrush ? 'draw.my-brush' : undefined}
              tabIndex={selected ? 0 : -1}
              onClick={() => selectTab(tab.id)}
              onKeyDown={(e) => handleTabKeyDown(e, index)}
              className={tabButtonClass(selected)}
            >
              {isMyBrush ? (
                <MyBrushIcon className="mx-auto h-3 w-3" />
              ) : (
                <span className="block truncate">{tab.label}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* タブパネル (排他 1 枚だけ描画。非アクティブタブの item / サムネは DOM 上にも
          存在しない = 上部コメント「メモ化」の計算スコープと一致させる)。 */}
      <div
        role="tabpanel"
        id={panelDomId(activeTab)}
        aria-labelledby={tabDomId(activeTab)}
        className="flex min-h-0 flex-col gap-1 overflow-y-auto overscroll-contain"
        style={{ maxHeight: BRUSH_PANEL_ITEMS_MAX_HEIGHT }}
      >
        {items.length === 0 ? (
          // マイブラシ (§3/§14.3): カスタムブラシ本体は別案件だが、開いたときだけ
          // 「準備中」の空状態を表示する (= 常設フッタは廃止、タブの 1 つに統合)。
          <p className="select-none text-[10px] text-[var(--color-text-muted)]">
            マイブラシ (準備中)
          </p>
        ) : (
          <div className={ITEMS_CONTAINER_CLASS}>
            {items.map((item) => {
              const active =
                pen === item.pen &&
                (item.texturePreset === undefined || item.texturePreset === texturePreset);
              return (
                <BrushPanelItemButton
                  key={item.key}
                  item={item}
                  active={active}
                  onSelect={selectItem}
                  canvasRef={(el) => {
                    if (el) canvasRefs.current.set(item.key, el);
                    else canvasRefs.current.delete(item.key);
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

/** タブボタンの Tailwind class。選択中を accent でハイライトする既存の慣行に合わせる。 */
const tabButtonClass = (selected: boolean): string =>
  `min-w-0 flex-1 rounded border px-1 py-0.5 text-center text-[10px] font-bold leading-none transition-colors ${
    selected
      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black'
      : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]'
  }`;
